import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { purchaseAPI } from '../../services/api';
import { useNotification } from '../../hooks/useNotification';

const PurchaseList = () => {
  const { t, i18n } = useTranslation();
  const { showSuccess, showError, showWarning } = useNotification();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    loadPurchases();
  }, [statusFilter]);

  const getItemName = (item) => {
    return i18n.language === 'ar' && item.item_name_ar ? item.item_name_ar : item.item_name;
  };

  const loadPurchases = async () => {
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const response = await purchaseAPI.getAll(params);
      setPurchases(response.data.data || response.data);
    } catch (error) {
      showError(t('messages.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('messages.confirmDelete'))) return;
    try {
      await purchaseAPI.delete(id);
      loadPurchases();
    } catch (error) {
      showError(t('messages.error'));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Ordered': return { bg: 'bg-green-500', text: 'text-green-700' };
      case 'Received': return { bg: 'bg-blue-500', text: 'text-blue-700' };
      case 'Pending': return { bg: 'bg-yellow-500', text: 'text-yellow-700' };
      case 'Cancelled': return { bg: 'bg-red-500', text: 'text-red-700' };
      default: return { bg: 'bg-gray-400', text: 'text-gray-700' };
    }
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = purchases.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(purchases.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('purchases.title')}</h2>
          <p className="text-gray-600 mt-1">{t('purchases.subtitle')}</p>
        </div>
        <Link
          to="/purchases/new"
          className="flex items-center px-4 py-2 bg-gradient-to-r bg-gray-900 text-white rounded-lg hover:bg-yellow-500  transition-all duration-200 shadow-lg hover:shadow-xl"
        >
          <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('purchases.addPurchase')}
        </Link>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="mb-6">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">{t('common.all')} {t('purchases.status')}</option>
            <option value="Pending">{t('purchases.statuses.Pending')}</option>
            <option value="Ordered">{t('purchases.statuses.Ordered')}</option>
            <option value="Received">{t('purchases.statuses.Received')}</option>
            <option value="Cancelled">{t('purchases.statuses.Cancelled')}</option>
          </select>
        </div>

        {/* Modern Card Layout */}
        <div className="space-y-3">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 rounded-lg text-xs font-medium text-gray-500 uppercase tracking-wider">
            <div className="col-span-2">{t('common.status')}</div>
            <div className="col-span-2">{t('purchases.supplier')}</div>
            <div className="col-span-3">{t('purchases.item')}</div>
            <div className="col-span-2">{t('purchases.quantity')}</div>
            <div className="col-span-2">{t('purchases.totalPrice')}</div>
            <div className="col-span-1 text-right">{t('common.actions')}</div>
          </div>

          {/* Card Items */}
          {currentItems.map((purchase) => {
            const statusColor = getStatusColor(purchase.status);
            return (
              <div key={purchase.id} className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 border border-gray-100">
                <div className="grid grid-cols-12 gap-4 px-6 py-4 items-center">
                  {/* Status */}
                  <div className="col-span-2 flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${statusColor.bg}`}></div>
                    <span className={`text-sm font-medium ${statusColor.text}`}>
                      {t(`purchases.statuses.${purchase.status}`)}
                    </span>
                  </div>

                  {/* Supplier */}
                  <div className="col-span-2">
                    <div className="text-sm font-semibold text-gray-900">{purchase.supplier?.name || 'N/A'}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      PO# {purchase.purchase_order_number}
                    </div>
                  </div>

                  {/* Item */}
                  <div className="col-span-3">
                    <div className="text-sm text-gray-900">
                      {purchase.inventory_item ? getItemName(purchase.inventory_item) : 'N/A'}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {new Date(purchase.purchase_date).toLocaleDateString()}
                    </div>
                  </div>

                  {/* Quantity */}
                  <div className="col-span-2">
                    <div className="text-sm font-medium text-gray-900">{purchase.quantity}</div>
                    <div className="text-xs text-gray-500">{purchase.inventory_item?.unit || ''}</div>
                  </div>

                  {/* Price */}
                  <div className="col-span-2">
                    <div className="text-sm font-semibold text-gray-900">
                      {t('common.currency')} {parseFloat(purchase.total_price || 0).toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-500">
                      @ {t('common.currency')} {parseFloat(purchase.unit_price || 0).toFixed(2)}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="col-span-1 flex items-center justify-end gap-2">
                    <Link
                      to={`/purchases/${purchase.id}`}
                      className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title={t('common.edit')}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </Link>
                    <button
                      onClick={() => handleDelete(purchase.id)}
                      className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title={t('common.delete')}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200 mt-4">
          <div className="text-sm text-gray-700">
            Show <span className="font-medium">{Math.min(indexOfFirstItem + 1, purchases.length)}</span> of{' '}
            <span className="font-medium">{purchases.length}</span> purchases
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            {[...Array(totalPages)].map((_, index) => (
              <button
                key={index + 1}
                onClick={() => paginate(index + 1)}
                className={`px-3 py-1 text-sm font-medium rounded-md ${
                  currentPage === index + 1
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {index + 1}
              </button>
            ))}
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
            <span className="text-sm text-gray-700 ml-2">
              {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, purchases.length)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseList;
